import React, { useState, useEffect, createContext, useContext, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { getAbsoluteCoverUrl, getPlaceholderCover } from './utils/imageUtils';
import { track, EVENT, resetAnalyticsSession } from './utils/analytics';
import { captureAttribution } from './utils/attribution';
// Runtime-safe API base — falls back to window.location.origin if the build
// was poisoned with a *.preview.emergentagent.com URL. See lib/apiBase.js.
import { BACKEND_URL, API_BASE } from './lib/apiBase';
// Capture UTM/source params on first paint — runs once at module load.
captureAttribution();
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { App as CapacitorApp } from '@capacitor/app';

// Initialize StatusBar for native platforms (Android/iOS)
const initStatusBar = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      // Set status bar to not overlay the WebView (Android)
      await StatusBar.setOverlaysWebView({ overlay: false });
      // Set dark content (light icons) for dark background
      await StatusBar.setStyle({ style: Style.Dark });
      // Set status bar background color to match app header
      if (Capacitor.getPlatform() === 'android') {
        await StatusBar.setBackgroundColor({ color: '#0F141B' });
      }
    } catch (e) {
      console.log('StatusBar setup:', e);
    }
  }
};
initStatusBar();

// Handle OAuth deep link redirects (Google/Apple Sign In) on Capacitor.
// Backend redirects to com.storyfyc.app://auth/callback?access_token=...&provider=...
// The intent-filter in AndroidManifest.xml (scheme: com.storyfyc.app) routes it back to the app.
const initDeepLinkAuthListener = () => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    CapacitorApp.addListener('appUrlOpen', (event) => {
      (async () => {
        try {
          const url = event?.url || '';
          if (!url.startsWith('com.storyfyc.app://')) return;
          // Parse token from query string OR hash fragment
          const after = url.replace('com.storyfyc.app://', '');
          const queryPart = after.includes('#') ? after.split('#')[1] : (after.includes('?') ? after.split('?')[1] : '');
          // Close the Custom Tab / SFSafariViewController opened by @capacitor/browser
          try {
            const { Browser } = await import('@capacitor/browser');
            await Browser.close();
          } catch { /* plugin may not be loaded on older builds */ }
          if (!queryPart) return;
          const params = new URLSearchParams(queryPart);
          const accessToken = params.get('access_token');
          const provider = params.get('provider');
          if (accessToken) {
            console.log(`[Auth] Deep link ${provider || 'oauth'} callback success`);
            localStorage.setItem('token', accessToken);
            localStorage.setItem('hasSession', 'true');
            // Reload to root so App mounts with authenticated state
            window.location.href = '/';
          } else {
            const error = params.get('error');
            console.log('[Auth] Deep link error:', error);
            window.location.href = `/login${error ? `?error=${error}` : '?error=no_token'}`;
          }
        } catch (e) {
          console.log('Deep link parse error:', e);
        }
      })();
    });
  } catch (e) {
    console.log('Deep link listener setup:', e);
  }
};
initDeepLinkAuthListener();

// Critical pages loaded immediately
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';

// Lazy load all other pages for faster initial load
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const AuthorPublicProfilePage = lazy(() => import('./pages/AuthorPublicProfilePage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const StoryWorkspacePage = lazy(() => import('./pages/admin/StoryWorkspacePage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const DiscoverPage = lazy(() => import('./pages/DiscoverPage'));
const GenrePage = lazy(() => import('./pages/GenrePage'));
const StoryDetailsPage = lazy(() => import('./pages/StoryDetailsPage'));
const BookReaderPage = lazy(() => import('./pages/BookReaderPage'));
const CommentsPage = lazy(() => import('./pages/CommentsPage'));
const LibraryPage = lazy(() => import('./pages/LibraryPage'));
const WalletPage = lazy(() => import('./pages/WalletPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelledPage = lazy(() => import('./pages/PaymentCancelledPage'));
const AuthorCenterPage = lazy(() => import('./pages/AuthorCenterPage'));
const CreateStoryPage = lazy(() => import('./pages/CreateStoryPage'));
const EditStoryPage = lazy(() => import('./pages/EditStoryPage'));
const WriterBenefitsPage = lazy(() => import('./pages/WriterBenefitsPage'));
const WritingPolicyPage = lazy(() => import('./pages/WritingPolicyPage'));
const ContestPage = lazy(() => import('./pages/ContestPage'));
const MonthlyContestPage = lazy(() => import('./pages/MonthlyContestPage'));
const ShifterContestPage = lazy(() => import('./pages/ShifterContestPage'));
const ReferralContestPage = lazy(() => import('./pages/ReferralContestPage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const NewStoriesPage = lazy(() => import('./pages/NewStoriesPage'));
const UpcomingStoriesPage = lazy(() => import('./pages/UpcomingStoriesPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const RankingsPage = lazy(() => import('./pages/RankingsPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const MarketingAssetsPage = lazy(() => import('./pages/MarketingAssetsPage'));
const PlayStoreCapture = lazy(() => import('./pages/marketing/PlayStoreCapture'));
const AccountDeletionPage = lazy(() => import('./pages/AccountDeletionPage'));
const BecomeAcquisitionEditorPage = lazy(() => import('./pages/BecomeAcquisitionEditorPage'));
const AEDashboardPage = lazy(() => import('./pages/AEDashboardPage'));
const AuthorMessagesPage = lazy(() => import('./pages/AuthorMessagesPage'));
const RewardsPage = lazy(() => import('./pages/RewardsPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));

import { NotificationBell } from './components/common/Notifications';
import { AEMessagesLink } from './components/common/AEMessagesLink';
import EmailSubscriptionPopup from './components/common/EmailSubscriptionPopup';
import OfflineBanner from './components/common/OfflineBanner';
import { NetworkProvider } from './context/NetworkContext';

// Loading spinner for lazy-loaded pages
const PageLoader = () => (
  <div className="app-safe-area bg-dark-bg flex items-center justify-center">
    <div className="text-center">
      <div className="w-10 h-10 border-3 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
      <p className="text-gray-400 text-sm">Loading...</p>
    </div>
  </div>
);

// Runtime-safe API base (resolved at top of file — see import on line ~10).
const API = API_BASE;

// Configure axios to handle errors gracefully
axios.interceptors.response.use(
  response => response,
  error => {
    if (error?.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('hasSession');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Stripe publishable key
const stripePromise = loadStripe('pk_live_51RA8ZSGxrZpsTVfqW7LaGUVBcrKGYqAmXJNb4lMfrY9HblMAY8dj0AD2mjDRuJZDmJHSvQxRY8npDNaMb6Io5PjW00HJxujYUh');

// Content Protection
// NOTE: The legacy document-level `useContentProtection` hook was removed.
// It blocked copy/right-click globally for ALL users — including admins —
// and conflicted with the page-scoped, role-aware `useReaderProtection` hook
// in `src/hooks/useReaderProtection.js` (used by BookReaderPage / StoryDetailsPage).
// Reader protection is now exclusively handled there.

// Auth Context
const AuthContext = createContext();

// Auth status: "loading" | "authenticated" | "unauthenticated"
const AuthProvider = ({ children }) => {
  // Check localStorage synchronously for instant UI decision
  const hasStoredSession = () => {
    return localStorage.getItem('token') && localStorage.getItem('hasSession') === 'true';
  };
  
  const [user, setUser] = useState(null);
  const [authStatus, setAuthStatus] = useState(hasStoredSession() ? 'loading' : (localStorage.getItem('token') ? 'loading' : 'unauthenticated'));
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [showReferralPopup, setShowReferralPopup] = useState(false);
  const [referralRates, setReferralRates] = useState(null);
  const [showStoryPopup, setShowStoryPopup] = useState(false);
  const [popupStories, setPopupStories] = useState([]);

  // For backwards compatibility
  const loading = authStatus === 'loading';

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchMe(token);
    } else {
      localStorage.removeItem('hasSession');
      setAuthStatus('unauthenticated');
    }
  }, []);

  const fetchMe = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      localStorage.setItem('hasSession', 'true');
      if (response.data?.id) localStorage.setItem('current_user_id', response.data.id);
      setAuthStatus('authenticated');
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('hasSession');
      localStorage.removeItem('current_user_id');
      setAuthStatus('unauthenticated');
    }
  };

  const refreshBalance = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(prev => prev ? { ...prev, coin_balance: response.data.coin_balance } : prev);
    } catch {}
  };

  // Listen for balance-updated events from child pages
  useEffect(() => {
    const handler = (e) => {
      if (e.detail !== undefined) {
        setUser(prev => prev ? { ...prev, coin_balance: e.detail } : prev);
      }
    };
    window.addEventListener('balance-updated', handler);
    return () => window.removeEventListener('balance-updated', handler);
  }, []);

  const checkEmailPopup = () => {
    const hasSeenPopup = localStorage.getItem('email_popup_shown');
    if (!hasSeenPopup) {
      setTimeout(() => setShowEmailPopup(true), 2000);
    }
  };

  const checkReferralPopup = async () => {
    // Use user-scoped key so the popup re-appears per user (fixes cross-user cache leak)
    const currentUserId = localStorage.getItem('current_user_id');
    const key = currentUserId ? `referral_popup_shown_${currentUserId}` : 'referral_popup_shown';
    const hasSeenReferralPopup = sessionStorage.getItem(key);
    if (hasSeenReferralPopup) return;

    // Fetch current bonus rates from backend so popup NEVER shows stale values
    try {
      const r = await axios.get(`${API}/referral/rates`);
      setReferralRates(r.data);
    } catch {
      setReferralRates({ '300_pack': 20, '1000_pack': 50, first_purchase_only: true });
    }
    setTimeout(() => {
      setShowReferralPopup(true);
      track(EVENT.REFERRAL_POPUP_VIEWED);
    }, 3000);
  };

  const checkStoryPopup = async () => {
    const hasSeenStoryPopup = sessionStorage.getItem('story_popup_shown');
    if (hasSeenStoryPopup) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stories/popup-stories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.length > 0) {
        setPopupStories(response.data);
        setTimeout(() => setShowStoryPopup(true), 4000);
      }
    } catch (err) {
      // Silent fail
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('hasSession', 'true');
      localStorage.setItem('current_user_id', response.data.user.id);
      setUser(response.data.user);
      setAuthStatus('authenticated');
      track(EVENT.LOGIN_SUCCESS, { user_id: response.data.user.id });
      // Skip reader/author popups for AEs — they live in their own portal.
      if (response.data.user.role !== 'AE') {
        checkEmailPopup();
        checkReferralPopup();
        checkStoryPopup();
      }
      return response.data;
    } catch (err) {
      track(EVENT.LOGIN_FAILURE, { reason: err.response?.status === 401 ? 'invalid_credentials' : 'server_error', status: err.response?.status || 0 });
      throw err;
    }
  };

  const signup = async (email, password, dateOfBirth, referralCode = null) => {
    track(EVENT.SIGNUP_STARTED, { has_referral: !!referralCode });
    const payload = { email, password, date_of_birth: dateOfBirth };
    // `referralCode` may be either a user-referral code (e.g. "ABC123") or an
    // Acquisition Editor code (prefixed "AE_"). The backend treats them as
    // separate fields so attribution can be locked at signup time.
    if (referralCode) {
      const code = String(referralCode).trim();
      if (code.toUpperCase().startsWith('AE_')) {
        payload.ae_ref = code.toUpperCase();
      } else {
        payload.referral_code = code;
      }
    }
    // Pick up any AE ref captured on landing pages (sticky in localStorage)
    // even if the user didn't pass one explicitly.
    if (!payload.ae_ref) {
      try {
        const stickyAe = localStorage.getItem('sfy_ae_ref');
        if (stickyAe && stickyAe.startsWith('AE_')) {
          payload.ae_ref = stickyAe;
        }
      } catch { /* ignore */ }
    }
    try {
      const response = await axios.post(`${API}/auth/signup`, payload);
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('hasSession', 'true');
      localStorage.setItem('current_user_id', response.data.user.id);
      setUser(response.data.user);
      setAuthStatus('authenticated');
      track(EVENT.SIGNUP_SUCCESS, { user_id: response.data.user.id, has_referral: !!referralCode });
      setTimeout(() => setShowEmailPopup(true), 2000);
      if (!referralCode) {
        checkReferralPopup();
      }
      return response.data;
    } catch (err) {
      track(EVENT.SIGNUP_FAILURE, { reason: err.response?.data?.detail?.slice(0, 50) || 'unknown', status: err.response?.status || 0 });
      throw err;
    }
  };

  const logout = () => {
    // Clear per-user popup flags so next user sees fresh popups
    const uid = localStorage.getItem('current_user_id');
    track(EVENT.LOGOUT, uid ? { user_id: uid } : {});
    if (uid) {
      sessionStorage.removeItem(`referral_popup_shown_${uid}`);
    }
    sessionStorage.removeItem('referral_popup_shown');
    sessionStorage.removeItem('story_popup_shown');
    sessionStorage.removeItem('analytics_session_id');
    // Wipe all user-scoped local state to prevent cross-user leakage on shared devices
    localStorage.removeItem('token');
    localStorage.removeItem('hasSession');
    localStorage.removeItem('current_user_id');
    localStorage.removeItem('audio_fab_hint_seen');
    localStorage.removeItem('readingProgress');
    localStorage.removeItem('reader_settings');
    localStorage.removeItem('galateanReaderSettings');
    // Wipe any draft keys (story/chapter drafts are user-scoped)
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith('draft_') || k.startsWith('chapter_draft_') || k.startsWith('story_draft_')) {
          localStorage.removeItem(k);
        }
      });
    } catch { /* ignore */ }
    // Flush any pending analytics and reset session id
    resetAnalyticsSession();
    setUser(null);
    setAuthStatus('unauthenticated');
    setShowReferralPopup(false);
    setShowEmailPopup(false);
    setShowStoryPopup(false);
  };

  const closeEmailPopup = () => setShowEmailPopup(false);
  
  const closeReferralPopup = () => {
    sessionStorage.setItem('referral_popup_shown', 'true');
    setShowReferralPopup(false);
  };
  
  const closeStoryPopup = () => {
    sessionStorage.setItem('story_popup_shown', 'true');
    setShowStoryPopup(false);
  };

  return (
    <AuthContext.Provider value={{ 
      user, login, signup, logout, loading, authStatus, refreshBalance,
      showEmailPopup, closeEmailPopup, 
      showReferralPopup, closeReferralPopup, referralRates,
      showStoryPopup, closeStoryPopup, popupStories
    }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// API helper
const apiCall = async (method, url, data = null) => {
  const token = localStorage.getItem('token');
  const config = {
    method,
    url: `${API}${url}`,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
  if (data) config.data = data;
  return axios(config);
};

// Header Component
const Header = () => {
  const { user, logout, authStatus } = useAuth();
  const navigate = useNavigate();
  const [showCategoriesDropdown, setShowCategoriesDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [genres, setGenres] = useState([]);

  useEffect(() => {
    loadCategoriesData();
  }, []);

  const loadCategoriesData = async () => {
    try {
      const [genresRes] = await Promise.all([axios.get(`${API}/genres`)]);
      setGenres(Array.isArray(genresRes.data) ? genresRes.data : []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  // Close mobile menu when navigating
  const handleNavigation = (path) => {
    navigate(path);
    setShowMobileMenu(false);
    setShowCategoriesDropdown(false);
  };

  // Auth button skeleton for loading state
  const AuthSkeleton = () => (
    <div className="flex items-center gap-3">
      <div className="w-16 h-8 bg-dark-bg rounded animate-pulse"></div>
      <div className="w-20 h-8 bg-dark-bg rounded animate-pulse"></div>
    </div>
  );

  return (
    <header className="bg-dark-card border-b border-dark-card sticky top-0 z-50 pt-safe" data-testid="app-header">
      <div className="container mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
        <Link to="/" className="hover:opacity-80 transition-opacity" data-testid="logo-link">
          <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            StoryfYC
          </span>
        </Link>
        
        {/* Mobile Menu Button */}
        <button 
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          className="md:hidden p-2 text-dark-text-secondary hover:text-dark-text"
          data-testid="mobile-menu-button"
        >
          {showMobileMenu ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>

        {/* Desktop Navigation */}
        {user ? (
          <nav className="hidden md:flex items-center gap-4 lg:gap-6">
            <div className="relative">
              <button
                onClick={() => setShowCategoriesDropdown(!showCategoriesDropdown)}
                onMouseEnter={() => setShowCategoriesDropdown(true)}
                className="text-dark-text-secondary hover:text-dark-text flex items-center gap-1 text-sm lg:text-base"
                data-testid="categories-dropdown-button"
              >
                Categories
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showCategoriesDropdown && (
                <div
                  className="absolute top-full right-0 mt-2 w-[320px] lg:w-[400px] bg-dark-card border border-purple-500/30 rounded-lg shadow-2xl py-3 z-50 max-h-[500px] overflow-y-auto"
                  onMouseLeave={() => setShowCategoriesDropdown(false)}
                  data-testid="categories-dropdown"
                >
                  <button onClick={() => handleNavigation('/discover')}
                    className="w-full px-4 py-2 border-b border-dark-bg mb-2 hover:bg-purple-500/10 transition-colors">
                    <h3 className="text-sm font-bold text-dark-accent hover:text-purple-300 text-left">BROWSE STORIES →</h3>
                  </button>
                  <button onClick={() => handleNavigation('/new-stories')}
                    className="w-full text-left px-6 py-3 hover:bg-purple-500/10 text-dark-text hover:text-dark-accent flex items-center gap-3 transition-colors">
                    <span className="text-xl">✨</span>
                    <div><div className="font-semibold">New Stories</div><div className="text-xs text-dark-text-secondary">Freshly published reads</div></div>
                  </button>
                  <button onClick={() => handleNavigation('/discover?filter=featured')}
                    className="w-full text-left px-6 py-3 hover:bg-purple-500/10 text-dark-text hover:text-dark-accent flex items-center gap-3 transition-colors">
                    <span className="text-xl">⭐</span>
                    <div><div className="font-semibold">Featured Stories</div><div className="text-xs text-dark-text-secondary">Hand-picked by our editors</div></div>
                  </button>
                  <button onClick={() => handleNavigation('/discover?filter=trending')}
                    className="w-full text-left px-6 py-3 hover:bg-purple-500/10 text-dark-text hover:text-dark-accent flex items-center gap-3 transition-colors">
                    <span className="text-xl">🔥</span>
                    <div><div className="font-semibold">Trending Stories</div><div className="text-xs text-dark-text-secondary">Most popular right now</div></div>
                  </button>
                  <button onClick={() => handleNavigation('/upcoming-stories')}
                    className="w-full text-left px-6 py-3 hover:bg-purple-500/10 text-dark-text hover:text-dark-accent flex items-center gap-3 transition-colors">
                    <span className="text-xl">🔮</span>
                    <div><div className="font-semibold">Coming Soon</div><div className="text-xs text-dark-text-secondary">Preview upcoming stories</div></div>
                  </button>
                  <div className="border-t border-dark-bg my-3"></div>
                  <div className="px-6 py-2"><h3 className="text-xs font-bold text-dark-accent mb-2">GENRES</h3></div>
                  <div className="grid grid-cols-2 gap-1 px-3">
                    {(Array.isArray(genres) ? genres : []).map((genre) => (
                      <button key={genre.id} onClick={() => handleNavigation(`/discover?genre=${genre.id}`)}
                        className="text-left px-3 py-2 hover:bg-purple-500/10 rounded text-dark-text hover:text-dark-accent text-sm transition-colors">
                        {genre.name}
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-dark-bg my-3"></div>
                  <button onClick={() => handleNavigation('/writer-benefits')}
                    className="w-full text-left px-6 py-3 hover:bg-purple-500/10 text-dark-accent font-semibold flex items-center gap-2 transition-colors">
                    <span className="text-xl">💎</span><div><div>Writer Benefits</div><div className="text-xs text-dark-text-secondary">Learn about earning opportunities</div></div>
                  </button>
                  <button onClick={() => handleNavigation('/contest')}
                    className="w-full text-left px-6 py-3 hover:bg-purple-500/10 text-dark-accent font-semibold flex items-center gap-2 transition-colors">
                    <span className="text-xl">🏆</span><div><div>Writing Contest</div><div className="text-xs text-dark-text-secondary">Earn bonus coins monthly!</div></div>
                  </button>
                  <button onClick={() => handleNavigation('/author')}
                    className="w-full text-left px-6 py-3 hover:bg-purple-500/10 text-dark-accent font-semibold flex items-center gap-2 transition-colors">
                    <span className="text-xl">✍️</span><div><div>Author Center</div><div className="text-xs text-dark-text-secondary">Start writing your story</div></div>
                  </button>
                </div>
              )}
            </div>
            <Link to="/profile" className="text-dark-text-secondary hover:text-dark-text flex items-center gap-1 text-sm lg:text-base" data-testid="nav-profile">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="hidden lg:inline">Profile</span>
            </Link>
            <Link to="/library" className="text-dark-text-secondary hover:text-dark-text text-sm lg:text-base" data-testid="nav-library">Library</Link>
            <Link to="/wallet" className="text-dark-text-secondary hover:text-dark-text text-sm lg:text-base" data-testid="nav-wallet">
              <span className="hidden lg:inline">Wallet </span>({user.coin_balance})
            </Link>
            <AEMessagesLink user={user} />
            <NotificationBell />
            {user.is_ae && (
              <Link
                to="/ae/dashboard"
                className="text-dark-accent hover:text-purple-400 text-sm lg:text-base"
                data-testid="nav-ae-center"
              >
                AE Center
              </Link>
            )}
            {user.role === 'ADMIN' && (
              <Link to="/admin" className="text-dark-accent hover:text-purple-400 text-sm lg:text-base" data-testid="nav-admin">Admin</Link>
            )}
            <button onClick={logout} className="bg-dark-accent text-white px-3 py-1.5 lg:px-4 lg:py-2 rounded-lg hover:bg-purple-700 text-sm lg:text-base" data-testid="logout-button">Logout</button>
          </nav>
        ) : authStatus === 'loading' ? (
          <div className="hidden md:block"><AuthSkeleton /></div>
        ) : (
          <div className="hidden md:flex gap-4">
            <button onClick={() => navigate('/login')} className="text-dark-text-secondary hover:text-dark-text" data-testid="login-button">Login</button>
            <button onClick={() => navigate('/signup')} className="bg-dark-accent text-white px-4 py-2 rounded-lg hover:bg-purple-700" data-testid="signup-button">Sign Up</button>
          </div>
        )}
      </div>

      {/* Mobile Menu Overlay */}
      {showMobileMenu && (
        <div className="md:hidden fixed inset-0 top-[57px] bg-dark-bg z-40 overflow-y-auto" data-testid="mobile-menu">
          <div className="p-4 space-y-2">
            {authStatus === 'loading' ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : user ? (
              <>
                {/* User Info */}
                <div className="bg-dark-card rounded-lg p-4 mb-4">
                  <p className="text-dark-text font-semibold">{user.email}</p>
                  <p className="text-dark-accent text-sm">{user.coin_balance} coins</p>
                </div>

                {/* Main Navigation */}
                <button onClick={() => handleNavigation('/discover')} className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3">
                  <span>📚</span> Discover Stories
                </button>
                <button onClick={() => handleNavigation('/new-stories')} className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3">
                  <span>✨</span> New Stories
                </button>
                <button onClick={() => handleNavigation('/upcoming-stories')} className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3">
                  <span>🔮</span> Coming Soon
                </button>
                <button onClick={() => handleNavigation('/profile')} className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3">
                  <span>👤</span> Profile
                </button>
                <button onClick={() => handleNavigation('/library')} className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3">
                  <span>📖</span> Library
                </button>
                <button onClick={() => handleNavigation('/wallet')} className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3">
                  <span>💰</span> Wallet
                </button>
                <button
                  onClick={() => handleNavigation('/messages')}
                  className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3"
                  data-testid="mobile-nav-messages"
                >
                  <span>💬</span> Messages
                </button>
                
                {/* Genres Section */}
                <div className="border-t border-dark-card my-4 pt-4">
                  <p className="text-dark-text-secondary text-xs font-semibold mb-2 px-4">GENRES</p>
                  <div className="grid grid-cols-2 gap-2">
                    {(Array.isArray(genres) ? genres : []).map((genre) => (
                      <button key={genre.id} onClick={() => handleNavigation(`/discover?genre=${genre.id}`)}
                        className="py-2 px-4 text-dark-text-secondary hover:text-dark-accent text-sm text-left">
                        {genre.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Author Section */}
                <div className="border-t border-dark-card my-4 pt-4">
                  <p className="text-dark-text-secondary text-xs font-semibold mb-2 px-4">FOR AUTHORS</p>
                  <button onClick={() => handleNavigation('/author')} className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3">
                    <span>✍️</span> Author Center
                  </button>
                  <button onClick={() => handleNavigation('/writer-benefits')} className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3">
                    <span>💎</span> Writer Benefits
                  </button>
                  <button onClick={() => handleNavigation('/contest')} className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3">
                    <span>🏆</span> Writing Contest
                  </button>
                  <button
                    onClick={() => handleNavigation('/become-acquisition-editor')}
                    className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3"
                    data-testid="mobile-menu-become-ae"
                  >
                    <span>📝</span> Become an Acquisition Editor
                  </button>
                </div>

                {/* AE Link — visible only to verified AEs */}
                {user.is_ae && (
                  <button
                    onClick={() => handleNavigation('/ae/dashboard')}
                    className="w-full text-left py-3 px-4 text-dark-accent hover:bg-dark-card rounded-lg flex items-center gap-3"
                    data-testid="mobile-menu-ae-center"
                  >
                    <span>🎯</span> AE Center
                  </button>
                )}

                {/* Admin Link */}
                {user.role === 'ADMIN' && (
                  <button onClick={() => handleNavigation('/admin')} className="w-full text-left py-3 px-4 text-dark-accent hover:bg-dark-card rounded-lg flex items-center gap-3">
                    <span>⚙️</span> Admin Dashboard
                  </button>
                )}

                {/* Logout */}
                <button 
                  onClick={() => { logout(); setShowMobileMenu(false); }} 
                  className="w-full mt-4 bg-red-500/20 text-red-400 py-3 px-4 rounded-lg text-center font-semibold"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                {/* Guest Navigation */}
                <button onClick={() => handleNavigation('/discover')} className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3">
                  <span>📚</span> Discover Stories
                </button>
                <button onClick={() => handleNavigation('/contest')} className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3">
                  <span>🏆</span> Writing Contest
                </button>
                <button onClick={() => handleNavigation('/writer-benefits')} className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3">
                  <span>💎</span> Writer Benefits
                </button>
                <button
                  onClick={() => handleNavigation('/become-acquisition-editor')}
                  className="w-full text-left py-3 px-4 text-dark-text hover:bg-dark-card rounded-lg flex items-center gap-3"
                  data-testid="mobile-menu-become-ae-guest"
                >
                  <span>📝</span> Become an Acquisition Editor
                </button>

                {/* Auth Buttons */}
                <div className="border-t border-dark-card my-4 pt-4 space-y-3">
                  <button onClick={() => handleNavigation('/login')} className="w-full bg-dark-card text-dark-text py-3 px-4 rounded-lg text-center font-semibold">
                    Login
                  </button>
                  <button onClick={() => handleNavigation('/signup')} className="w-full bg-dark-accent text-white py-3 px-4 rounded-lg text-center font-semibold">
                    Sign Up
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

// Story Card Component
const StoryCard = ({ story, onClick }) => (
  <div onClick={onClick} className="flex-shrink-0 w-48 cursor-pointer group" data-testid={`story-card-${story.id}`}>
    <div className="relative overflow-hidden rounded-lg mb-3 transition-transform group-hover:scale-105">
      <img src={getAbsoluteCoverUrl(story.cover)} alt={story.title} className="w-full h-72 object-cover"
        onError={(e) => { e.target.src = getPlaceholderCover(); }} />
      <span className={`absolute top-2 right-2 px-2 py-0.5 rounded text-[10px] font-semibold ${story.is_completed ? 'bg-blue-500/90 text-white' : 'bg-orange-500/90 text-white'}`}
        data-testid={`story-card-status-${story.id}`}>
        {story.is_completed ? '✓ Completed' : 'Ongoing'}
      </span>
    </div>
    <h3 className="text-dark-text font-bold text-sm mb-1 line-clamp-2" data-testid="story-title">{story.title}</h3>
    <p className="text-dark-text-secondary text-xs">by Author</p>
  </div>
);

// Footer Component
const Footer = () => {
  const navigate = useNavigate();
  const hotGenres = [
    { name: 'Romantasy', path: '/genre/romantasy' },
    { name: 'Shifters', path: '/genre/shifters' },
    { name: 'Dark Romance', path: '/genre/dark-romance' },
    { name: 'Contemporary', path: '/genre/contemporary' }
  ];
  const resources = [
    { name: 'Writer Benefits', path: '/writer-benefits' },
    { name: 'Writing Contest', path: '/contest' },
    { name: 'Author Center', path: '/author' }
  ];
  const community = [
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/about' },
    { name: 'Privacy Policy', path: '/privacy' }
  ];
  
  return (
    <footer className="bg-dark-card mt-8 sm:mt-16 border-t border-dark-bg" data-testid="app-footer">
      <div className="container mx-auto px-4 py-6 sm:py-10">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6 sm:gap-8 mb-6 sm:mb-8">
          <div className="col-span-2 sm:col-span-1">
            <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">StoryfYC</span>
            <p className="text-dark-text-secondary text-[10px] sm:text-xs mt-2">Your destination for addictive romance fiction.</p>
            <p className="text-dark-text-secondary text-[10px] sm:text-xs mt-1">by Fyctiona Mediatech Ltd</p>
          </div>
          <div>
            <h4 className="text-dark-text font-semibold text-xs sm:text-sm mb-2 sm:mb-3">Hot Genres</h4>
            <ul className="space-y-1 sm:space-y-2">
              {hotGenres.map((genre) => (
                <li key={genre.name}><button onClick={() => navigate(genre.path)} className="text-dark-text-secondary hover:text-dark-accent text-[10px] sm:text-xs transition-colors">{genre.name}</button></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-dark-text font-semibold text-xs sm:text-sm mb-2 sm:mb-3">Resources</h4>
            <ul className="space-y-1 sm:space-y-2">
              {resources.map((item) => (
                <li key={item.name}><button onClick={() => navigate(item.path)} className="text-dark-text-secondary hover:text-dark-accent text-[10px] sm:text-xs transition-colors">{item.name}</button></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-dark-text font-semibold text-xs sm:text-sm mb-2 sm:mb-3">Community</h4>
            <ul className="space-y-1 sm:space-y-2">
              {community.map((item) => (
                <li key={item.name}><button onClick={() => navigate(item.path)} className="text-dark-text-secondary hover:text-dark-accent text-[10px] sm:text-xs transition-colors">{item.name}</button></li>
              ))}
            </ul>
            <div className="mt-3 sm:mt-4 space-y-1">
              <a href="mailto:support@storyfyc.com" className="text-dark-text-secondary hover:text-dark-accent text-[10px] sm:text-xs block">📧 support@storyfyc.com</a>
              <a href="mailto:creators@storyfyc.com" className="text-dark-text-secondary hover:text-dark-accent text-[10px] sm:text-xs block">✍️ creators@storyfyc.com</a>
            </div>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <h4 className="text-dark-text font-semibold text-xs sm:text-sm mb-2 sm:mb-3">Follow Us</h4>
            <div className="flex gap-2 sm:gap-3">
              <a href="https://facebook.com/storyfyc" target="_blank" rel="noopener noreferrer" className="w-7 h-7 sm:w-9 sm:h-9 bg-dark-bg rounded-full flex items-center justify-center hover:bg-dark-accent/30 transition-colors group" aria-label="Facebook">
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-dark-text-secondary group-hover:text-dark-accent" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://instagram.com/storyfyc" target="_blank" rel="noopener noreferrer" className="w-7 h-7 sm:w-9 sm:h-9 bg-dark-bg rounded-full flex items-center justify-center hover:bg-dark-accent/30 transition-colors group" aria-label="Instagram">
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-dark-text-secondary group-hover:text-dark-accent" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a href="https://youtube.com/@storyfyc" target="_blank" rel="noopener noreferrer" className="w-7 h-7 sm:w-9 sm:h-9 bg-dark-bg rounded-full flex items-center justify-center hover:bg-dark-accent/30 transition-colors group" aria-label="YouTube">
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-dark-text-secondary group-hover:text-red-500" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="https://tiktok.com/@storyfyc" target="_blank" rel="noopener noreferrer" className="w-7 h-7 sm:w-9 sm:h-9 bg-dark-bg rounded-full flex items-center justify-center hover:bg-dark-accent/30 transition-colors group" aria-label="TikTok">
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-dark-text-secondary group-hover:text-dark-accent" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-dark-bg pt-4 sm:pt-6">
          <div className="text-center text-dark-text-secondary text-[10px] sm:text-xs space-y-1">
            <p>© 2026 Storyfyc by Fyctiona Mediatech Ltd (Company No. 17003903), Registered in England & Wales.</p>
            <p>Storyfyc is a trading name of Fyctiona Mediatech Ltd.</p>
          </div>
          <div className="flex justify-center gap-3 mt-2">
            <button onClick={() => navigate('/privacy')} className="text-dark-text-secondary hover:text-dark-accent text-[10px] sm:text-xs underline">Privacy Policy</button>
            <span className="text-dark-text-secondary text-[10px] sm:text-xs">|</span>
            <button onClick={() => navigate('/terms')} className="text-dark-text-secondary hover:text-dark-accent text-[10px] sm:text-xs underline">Terms & Conditions</button>
            <span className="text-dark-text-secondary text-[10px] sm:text-xs">|</span>
            <button
              onClick={() => navigate('/become-acquisition-editor')}
              data-testid="footer-become-ae-link"
              className="text-dark-text-secondary hover:text-dark-accent text-[10px] sm:text-xs underline"
            >
              Become an Acquisition Editor
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

// AdSense Configuration
const ADSENSE_CONFIG = {
  publisherId: 'ca-pub-2899983449421096',
  testMode: true,
  adSlots: {
    'home-top': '1234567890',
    'home-bottom': '1234567891',
    'discover-bottom': '1234567892',
    'reader-bottom': '1234567893',
    'reader-between-chapters': '1234567894',
    'story-sidebar': '1234567895',
    'story-after-comments': '1234567896',
    'library-between': '1234567897',
    'author-center-sidebar': '1234567898',
    'contest-bottom': '1234567899',
    'chapter-list-between': '1234567900',
  }
};

// Ad Banner Component
const AdBanner = ({ placement, size = 'auto', className = '' }) => {
  const adRef = React.useRef(null);
  const [adError, setAdError] = React.useState(false);
  const sizeStyles = {
    'leaderboard': { minHeight: '90px', maxWidth: '728px' },
    'rectangle': { minHeight: '250px', maxWidth: '300px' },
    'auto': { minHeight: '90px' }
  };
  const currentStyle = sizeStyles[size] || sizeStyles['auto'];

  React.useEffect(() => {
    if (window.adsbygoogle && adRef.current) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        setAdError(true);
      }
    }
  }, []);

  if (ADSENSE_CONFIG.testMode || adError || !window.adsbygoogle) {
    return (
      <div className={`bg-gradient-to-r from-purple-900/20 to-pink-900/20 rounded-lg p-4 text-center border border-purple-500/20 ${className}`}
        style={currentStyle} data-testid={`ad-banner-${placement}`}>
        <div className="flex items-center justify-center gap-2 mb-2">
          <span className="text-purple-300/70 text-xs font-medium">Advertisement</span>
        </div>
        <div className="bg-dark-card/30 rounded-md p-3">
          <p className="text-dark-text-secondary text-xs">Your ad could appear here</p>
          <p className="text-dark-text-secondary/50 text-[10px] mt-1">Placement: {placement}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`ad-container rounded-lg overflow-hidden ${className}`} data-testid={`ad-banner-${placement}`}>
      <p className="text-[10px] text-dark-text-secondary/50 text-center mb-1">Advertisement</p>
      <ins ref={adRef} className="adsbygoogle" style={{ display: 'block', ...currentStyle }}
        data-ad-client={ADSENSE_CONFIG.publisherId}
        data-ad-slot={ADSENSE_CONFIG.adSlots[placement] || ''}
        data-ad-format={size === 'auto' ? 'auto' : 'rectangle'}
        data-full-width-responsive="true"
        data-adtest={ADSENSE_CONFIG.testMode ? 'on' : 'off'} />
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="app-safe-area flex items-center justify-center">
        <p className="text-dark-text">Loading...</p>
      </div>
    );
  }

  return user ? children : null;
};

// Route guard for AE-only pages (e.g., /ae/dashboard). Redirects
// non-AEs to the homepage so a stale bookmark or shared link never
// triggers a backend 500 by hitting /api/ae/me without an AE profile.
const RequireAE = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="app-safe-area flex items-center justify-center">
        <p className="text-dark-text">Loading...</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_ae) return <Navigate to="/" replace />;
  return children;
};

// Removed-route redirect (Feb 2026). The /analytics page was deleted as
// part of the strict admin-controlled author data policy. Visitors are
// silently routed to a safe destination instead of seeing a blank screen.
const RemovedAnalyticsRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="app-safe-area flex items-center justify-center">
        <p className="text-dark-text">Loading...</p>
      </div>
    );
  }
  // `replace` keeps the browser history clean (no back-button loop into
  // the dead route).
  return <Navigate to={user ? '/author?tab=earnings' : '/'} replace />;
};

// Referral Tracker Component
const ReferralTracker = () => {
  const location = window.location;
  
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const refCode = urlParams.get('ref');
    
    if (refCode) {
      axios.post(`${API}/referral/track/${refCode}`, null, {
        params: { is_purchase: false }
      }).catch(err => console.log('Referral tracking error:', err));
      sessionStorage.setItem('referral_code', refCode);
    }
  }, [location.search]);
  
  return null;
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NetworkProvider>
          <OfflineBanner />
          <AppContent />
        </NetworkProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

// App Content with access to Auth Context
function AppContent() {
  const { showEmailPopup, closeEmailPopup, showReferralPopup, closeReferralPopup, referralRates, showStoryPopup, closeStoryPopup, popupStories, loading } = useAuth();
  const navigate = useNavigate();
  
  const handleViewReferralLink = () => {
    closeReferralPopup();
    navigate('/wallet');
  };
  
  const handleViewStory = (storyId) => {
    closeStoryPopup();
    navigate(`/story/${storyId}`);
  };
  
  // Show global loader while auth is being checked (prevents login page flash)
  if (loading) {
    return (
      <div className="app-safe-area flex items-center justify-center bg-dark-bg">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }
  
  return (
    <>
      <ReferralTracker />
      <div className="app-safe-area bg-dark-bg">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<LoginPage useAuth={useAuth} />} />
            <Route path="/signup" element={<SignupPage useAuth={useAuth} />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/account-deletion" element={<AccountDeletionPage />} />
            <Route path="/rewards" element={<Navigate to="/wallet" replace />} />
            <Route path="/leaderboard" element={<LeaderboardPage Header={Header} Footer={Footer} />} />
            <Route path="/marketing-assets" element={<ProtectedRoute><MarketingAssetsPage Header={Header} Footer={Footer} /></ProtectedRoute>} />
            <Route path="/marketing/play-store" element={<PlayStoreCapture />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/apple/callback" element={<AuthCallback />} />
            <Route path="/" element={<HomePage Header={Header} Footer={Footer} useAuth={useAuth} />} />
            <Route path="/discover" element={<DiscoverPage Header={Header} Footer={Footer} />} />
            <Route path="/rankings" element={<RankingsPage Header={Header} />} />
            <Route path="/genre/:genreId" element={<GenrePage Header={Header} Footer={Footer} />} />
            <Route path="/contest" element={<ContestPage Header={Header} Footer={Footer} useAuth={useAuth} />} />
            <Route path="/contest/monthly" element={<MonthlyContestPage Header={Header} Footer={Footer} useAuth={useAuth} />} />
            <Route path="/contest/shifter" element={<ShifterContestPage Header={Header} Footer={Footer} useAuth={useAuth} />} />
            <Route path="/contest/referrals" element={<ReferralContestPage Header={Header} Footer={Footer} />} />
            <Route path="/about" element={<AboutUsPage Header={Header} Footer={Footer} />} />
            <Route path="/privacy" element={<PrivacyPolicyPage Header={Header} Footer={Footer} />} />
            <Route path="/terms" element={<TermsPage Header={Header} Footer={Footer} />} />
            <Route path="/author/:authorId" element={<AuthorPublicProfilePage Header={Header} Footer={Footer} />} />
            {/* /analytics route was removed (Feb 2026) — author analytics no longer exposed.
                Redirect quietly so bookmarks / cached links never land on a blank page. */}
            <Route path="/analytics" element={<RemovedAnalyticsRedirect />} />
            <Route path="/analytics/*" element={<RemovedAnalyticsRedirect />} />
            <Route path="/writer-benefits" element={<WriterBenefitsPage Header={Header} Footer={Footer} />} />
            <Route path="/writing-policy" element={<WritingPolicyPage Header={Header} Footer={Footer} />} />
            <Route path="/story/:id" element={<StoryDetailsPage Header={Header} Footer={Footer} useAuth={useAuth} />} />
            <Route path="/book/:storyId" element={<ProtectedRoute><BookReaderPage Header={Header} useAuth={useAuth} /></ProtectedRoute>} />
            <Route path="/book/:storyId/read/:chapterId" element={<ProtectedRoute><BookReaderPage Header={Header} useAuth={useAuth} /></ProtectedRoute>} />
            <Route path="/comments/:storyId" element={<ProtectedRoute><CommentsPage useAuth={useAuth} /></ProtectedRoute>} />
            {/* Legacy redirect — old chapter-scoped route now flows through universal CommentsPage */}
            <Route path="/chapter/:chapterId/comments" element={<Navigate to="/" replace />} />
            <Route path="/new-stories" element={<NewStoriesPage Header={Header} Footer={Footer} />} />
            <Route path="/upcoming-stories" element={<UpcomingStoriesPage Header={Header} Footer={Footer} />} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage Header={Header} Footer={Footer} useAuth={useAuth} /></ProtectedRoute>} />
            <Route path="/library" element={<ProtectedRoute><LibraryPage Header={Header} Footer={Footer} /></ProtectedRoute>} />
            <Route path="/wallet" element={<ProtectedRoute><WalletPage Header={Header} Footer={Footer} useAuth={useAuth} /></ProtectedRoute>} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage Header={Header} useAuth={useAuth} /></ProtectedRoute>} />
            <Route path="/payment/cancelled" element={<PaymentCancelledPage Header={Header} Footer={Footer} />} />
            <Route path="/author" element={<ProtectedRoute><AuthorCenterPage Header={Header} Footer={Footer} useAuth={useAuth} /></ProtectedRoute>} />
            <Route path="/author-center" element={<ProtectedRoute><AuthorCenterPage Header={Header} Footer={Footer} useAuth={useAuth} /></ProtectedRoute>} />
            <Route path="/author/create" element={<ProtectedRoute><CreateStoryPage Header={Header} Footer={Footer} /></ProtectedRoute>} />
            <Route path="/author/story/:id" element={<ProtectedRoute><EditStoryPage Header={Header} Footer={Footer} /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage Header={Header} /></ProtectedRoute>} />
            <Route path="/admin/stories/:storyId/workspace" element={<ProtectedRoute><StoryWorkspacePage /></ProtectedRoute>} />
            <Route path="/become-acquisition-editor" element={<BecomeAcquisitionEditorPage Header={Header} Footer={Footer} />} />
            <Route path="/ae/dashboard" element={<RequireAE><AEDashboardPage useAuth={useAuth} /></RequireAE>} />
            <Route path="/messages" element={<ProtectedRoute><AuthorMessagesPage Header={Header} Footer={Footer} /></ProtectedRoute>} />
          </Routes>
        </Suspense>
      </div>
      
      {/* Email Subscription Popup */}
      {showEmailPopup && <EmailSubscriptionPopup onClose={closeEmailPopup} />}
      
      {/* Promoted Stories Popup */}
      {showStoryPopup && popupStories.length > 0 && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm" data-testid="story-popup-overlay">
          <div className="bg-dark-card rounded-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-300">
            <div className="flex justify-between items-start mb-4">
              <div>
                <span className="text-xs font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-1 rounded-full">NEW FOR YOU</span>
                <h3 className="text-xl font-bold text-white mt-2">Check Out These Stories!</h3>
              </div>
              <button onClick={closeStoryPopup} className="text-gray-400 hover:text-white p-1" data-testid="close-story-popup">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {popupStories.slice(0, 3).map((story) => (
                <button
                  key={story.id}
                  onClick={() => handleViewStory(story.id)}
                  className="w-full flex items-start gap-3 bg-dark-bg hover:bg-dark-bg/80 rounded-xl p-3 transition-all text-left group"
                  data-testid={`popup-story-${story.id}`}
                >
                  <img 
                    src={getAbsoluteCoverUrl(story.cover)} 
                    alt={story.title}
                    className="w-16 h-24 object-cover rounded-lg flex-shrink-0"
                    onError={(e) => { e.target.src = getPlaceholderCover(); }}
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-white group-hover:text-purple-400 transition-colors line-clamp-1">{story.title}</h4>
                    <p className="text-dark-text-secondary text-sm line-clamp-2 mt-1">{story.blurb}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-dark-text-secondary">
                      <span className="flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                        </svg>
                        {story.views || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd"/>
                        </svg>
                        {story.likes_count || 0}
                      </span>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-500 group-hover:text-purple-400 flex-shrink-0 mt-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
            
            <button 
              onClick={() => { closeStoryPopup(); navigate('/discover'); }}
              className="w-full mt-4 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:opacity-90 transition-all"
              data-testid="browse-all-stories-button"
            >
              Browse All Stories
            </button>
          </div>
        </div>
      )}
      
      {/* Referral Program Popup */}
      {showReferralPopup && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm" data-testid="referral-popup">
          <div className="bg-dark-card rounded-2xl max-w-sm w-full p-6 text-center animate-in fade-in zoom-in duration-300">
            <div className="text-5xl mb-4">🎁</div>
            <h3 className="text-xl font-bold text-white mb-2">Invite Friends & Earn Coins!</h3>
            <p className="text-dark-text-secondary mb-4">
              Earn up to <span className="text-purple-400 font-bold" data-testid="referral-max-bonus">{referralRates?.['1000_pack'] ?? 50} bonus coins</span> when a friend makes their first coin purchase!
            </p>
            <div className="bg-dark-bg p-3 rounded-lg mb-2 text-sm">
              <div className="flex justify-between text-dark-text-secondary mb-1">
                <span>Friend buys 300 pack</span>
                <span className="text-purple-400 font-semibold" data-testid="referral-rate-300">+{referralRates?.['300_pack'] ?? 20} coins</span>
              </div>
              <div className="flex justify-between text-dark-text-secondary">
                <span>Friend buys 1000 pack</span>
                <span className="text-pink-400 font-semibold" data-testid="referral-rate-1000">+{referralRates?.['1000_pack'] ?? 50} coins</span>
              </div>
            </div>
            <p className="text-[11px] text-dark-text-secondary italic mb-4">One-time bonus on your friend's first qualifying purchase.</p>
            <div className="flex gap-3">
              <button
                onClick={handleViewReferralLink}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-all"
                data-testid="referral-view-link"
              >
                View My Referral Link
              </button>
              <button
                onClick={closeReferralPopup}
                className="px-4 py-3 bg-dark-bg text-dark-text-secondary rounded-lg hover:bg-dark-card"
                data-testid="referral-close"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default App;
